import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import type {
  CapabilityUnavailableBehavior,
  DeploymentCapability
} from '@/config/deployment/deployment.types'
import { useDeploymentStore } from '@/store/deployment.store'

export type CapabilityGateState = {
  enabled: boolean
  disabled: boolean
  readOnly: boolean
  unavailableBehavior: CapabilityUnavailableBehavior
  message?: string
}

type CapabilityGateProps = {
  capability: DeploymentCapability
  children: ReactNode | ((state: CapabilityGateState) => ReactNode)
  fallback?: ReactNode
  defaultRedirectTo?: string
}

function renderChild(
  children: CapabilityGateProps['children'],
  state: CapabilityGateState
): ReactNode {
  if (typeof children === 'function') {
    return children(state)
  }

  if (!isValidElement(children) || state.enabled) {
    return children
  }

  // 静态 children 只能安全地修改其根节点；复杂表单应使用 render prop 自行传递状态。
  return cloneElement(children as ReactElement<Record<string, unknown>>, {
    'aria-disabled': state.disabled || undefined,
    'aria-readonly': state.readOnly || undefined,
    disabled: state.disabled || undefined,
    readOnly: state.readOnly || undefined
  })
}

/**
 * 按部署画像控制能力入口。对于 disabled、tooltip、readonly 场景，推荐使用
 * render prop 将 disabled、readOnly 和 message 显式传给具体的 UI 控件。
 */
export function CapabilityGate({
  capability,
  children,
  fallback = null,
  defaultRedirectTo = '/dashboard'
}: CapabilityGateProps) {
  const policy = useDeploymentStore(state => state.getCapabilityPolicy(capability))
  const state: CapabilityGateState = {
    enabled: policy.enabled,
    disabled:
      !policy.enabled &&
      (policy.unavailableBehavior === 'disabled' || policy.unavailableBehavior === 'tooltip'),
    readOnly: !policy.enabled && policy.unavailableBehavior === 'readonly',
    unavailableBehavior: policy.unavailableBehavior,
    message: policy.message
  }

  if (state.enabled) {
    return <>{renderChild(children, state)}</>
  }

  // hidden 不挂载子节点，避免被关闭的模块在首屏发起请求或产生页面闪现。
  if (state.unavailableBehavior === 'hidden') {
    return <>{fallback}</>
  }

  // redirect 在子节点渲染前执行；使用该策略时组件必须处于 Router 上下文中。
  if (state.unavailableBehavior === 'redirect') {
    return <Navigate to={policy.redirectTo ?? defaultRedirectTo} replace />
  }

  const content = renderChild(children, state)
  if (state.unavailableBehavior === 'tooltip' && state.message) {
    return <span title={state.message}>{content}</span>
  }

  return <>{content}</>
}
